import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

const COOKIE_NAME = "frayze_exit_dismissed";
const SUPPRESSION_DAYS = 30;
const MOBILE_SCROLL_THRESHOLD = 0.5; // 50% scroll
const MOBILE_TIME_THRESHOLD = 60000; // 60 seconds

// Pages where the popup should NEVER show (high-intent pages)
const EXCLUDED_PATHS = [
  "/pricing",
  "/contact",
  "/book-consultation",
  "/digital-audit",
  "/careers",
  "/onboarding",
  "/confirmation",
  "/thank-you",
  "/website-launch-program",
];

function isMobile(): boolean {
  return window.innerWidth < 768;
}

function isExcluded(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "");
  return EXCLUDED_PATHS.some((p) => normalized === p || normalized.startsWith(p + "/"));
}

function isSuppressed(): boolean {
  try {
    const dismissed = localStorage.getItem(COOKIE_NAME);
    if (!dismissed) return false;
    const expiry = parseInt(dismissed, 10);
    if (Date.now() < expiry) return true;
    localStorage.removeItem(COOKIE_NAME);
    return false;
  } catch {
    return false;
  }
}

function setSuppression(): void {
  try {
    const expiry = Date.now() + SUPPRESSION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(COOKIE_NAME, expiry.toString());
  } catch {
    // localStorage unavailable
  }
}

export const useExitIntent = () => {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const location = useLocation();

  const trigger = useCallback(() => {
    if (hasTriggered || isSuppressed() || isExcluded(location.pathname)) return;
    setShowExitIntent(true);
    setHasTriggered(true);
  }, [hasTriggered, location.pathname]);

  useEffect(() => {
    // Don't set up listeners if already triggered, suppressed, or excluded
    if (hasTriggered || isSuppressed() || isExcluded(location.pathname)) return;

    const mobile = isMobile();

    if (!mobile) {
      // Desktop: exit intent (mouse leaves top of viewport)
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0) trigger();
      };
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => document.removeEventListener("mouseleave", handleMouseLeave);
    } else {
      // Mobile: 50% scroll OR 60s on page (whichever first)
      let scrollFired = false;
      let timerId: ReturnType<typeof setTimeout> | null = null;

      const handleScroll = () => {
        if (scrollFired) return;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight > 0 && window.scrollY / scrollHeight >= MOBILE_SCROLL_THRESHOLD) {
          scrollFired = true;
          trigger();
        }
      };

      timerId = setTimeout(() => {
        trigger();
      }, MOBILE_TIME_THRESHOLD);

      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (timerId) clearTimeout(timerId);
      };
    }
  }, [hasTriggered, location.pathname, trigger]);

  const closeExitIntent = useCallback(() => {
    setShowExitIntent(false);
    setSuppression();
  }, []);

  return { showExitIntent, closeExitIntent };
};